import { ERROR_CODE } from "contracts/enums/api";
import { createEffect, createSignal } from "solid-js";
import { ApiError } from "../../store/api-client";
import { dateAndTimeParts } from "../../ui/boundaries/datetime-input.parser";
import {
  localDateTimeInputToUtcIso,
  toLocalDateTimeInputValue,
} from "../../ui/format";
import type { WebsiteContext } from "../website/store";

/** Schedule (or cancel) the next scan; ported from the retired old-shell top bar. */
export function ScanScheduler(props: { context: WebsiteContext }) {
  const data = () => props.context.data();
  const [open, setOpen] = createSignal(false);
  const [date, setDate] = createSignal("");
  const [time, setTime] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  createEffect(() => {
    const localValue = toLocalDateTimeInputValue(
      data()?.scanSchedule.nextScanAt ?? null,
    );
    const { date: nextDate, time: nextTime } = dateAndTimeParts(localValue);
    setDate(nextDate);
    setTime(nextTime);
  });

  const run = async (action: () => Promise<void>, failure: string) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      setOpen(false);
    } catch (err) {
      setError(scheduleErrorMessage(err, failure));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="scan-scheduler">
      <button
        class="btn-schedule"
        type="button"
        aria-expanded={open()}
        onClick={() => {
          setError(null);
          setOpen(!open());
        }}
      >
        Schedule
      </button>
      {open() ? (
        <div class="scan-popover">
          <label class="scan-field">
            <span>Date</span>
            <input
              class="scan-date"
              type="date"
              value={date()}
              onInput={(event) => setDate(event.currentTarget.value)}
              disabled={saving()}
            />
          </label>
          <label class="scan-field">
            <span>Time</span>
            <input
              class="scan-time"
              type="time"
              value={time()}
              onInput={(event) => setTime(event.currentTarget.value)}
              disabled={saving()}
            />
          </label>
          {error() ? <span class="scan-error">{error()}</span> : null}
          <div class="scan-actions">
            {data()?.scanSchedule.mode === "scheduled" ? (
              <button
                class="btn-cancel-scan"
                type="button"
                disabled={saving()}
                onClick={() =>
                  void run(
                    () => props.context.clearScanSchedule(),
                    "Unable to cancel scan",
                  )
                }
              >
                Clear
              </button>
            ) : null}
            <button
              class="btn-save-scan"
              type="button"
              disabled={!date() || !time() || saving()}
              onClick={() =>
                void run(
                  () =>
                    props.context.setScanSchedule({
                      nextScanAt: localDateTimeInputToUtcIso(
                        `${date()}T${time()}`,
                      ),
                    }),
                  "Unable to schedule scan",
                )
              }
            >
              Save
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// The backend already says WHICH failure this is, in its error envelope; the
// wording of the message is not the signal.
function scheduleErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.code === ERROR_CODE.UNAUTHORIZED) {
    return "Admin access required";
  }
  if (!(err instanceof Error)) {
    return fallback;
  }
  return err.message;
}
